import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Eye, Loader, RefreshCcw } from 'lucide-react';
import api from '../../api';
import Alert from '../../components/Alert/Alert';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../contexts/AuthProvider';

const STANDARD_OPTIONS = ['ISO', 'ISCC', 'LVV', 'LATIK'];
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_FILTERS = {
  standard: '',
  dateFrom: '',
  dateTo: '',
};

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatAmount = (value, currencyCode = 'IDR') => {
  const numericValue = Number(value ?? 0);
  try {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: currencyCode || 'IDR',
      maximumFractionDigits: 0,
    }).format(Number.isFinite(numericValue) ? numericValue : 0);
  } catch (error) {
    return `${currencyCode || 'IDR'} ${Number.isFinite(numericValue) ? numericValue.toLocaleString('id-ID') : '0'}`;
  }
};

const buildClientName = (certificate = {}) => (
  certificate.clientName
  || certificate.companyName
  || certificate.project?.clientName
  || certificate.project?.companyName
  || certificate.client?.name
  || certificate.company?.name
  || '—'
);

const buildStandardLabel = (certificate = {}) => {
  if (Array.isArray(certificate.standards) && certificate.standards.length > 0) {
    return certificate.standards.filter(Boolean).join(', ');
  }
  return certificate.standard
    || certificate.standardName
    || certificate.project?.standard
    || certificate.project?.standardName
    || '—';
};

const buildProjectCode = (certificate = {}) => (
  certificate.projectCode
  || certificate.project?.projectCode
  || certificate.project?.code
  || certificate.projectNumber
  || certificate.certificateNumber
  || '—'
);

const resolveProjectId = (certificate = {}) => (
  certificate.projectId
  || certificate.project?.id
  || certificate.project?.projectId
  || certificate.id
  || null
);

const resolveCertificateNumber = (certificate = {}) => (
  certificate.certificateNumber
  || certificate.certificateNo
  || certificate.barcodeValue
  || certificate.projectCode
  || 'PENDING'
);

const hasPreviewAssets = (certificate = {}) => Boolean(
  certificate.barcode
  || certificate.barcodeData
  || certificate.barcodeUrl
  || certificate.barcodeValue
  || certificate.qrCode
  || certificate.qrCodeUrl
  || certificate.qrValue
  || certificate.confirmedAt
  || String(certificate.paymentStatus || '').toUpperCase() === 'CONFIRMED'
);

const normalizeListResponse = (payload, pageSize) => {
  const root = payload?.data ?? payload ?? {};
  const container = Array.isArray(root)
    ? { content: root, totalElements: root.length, totalPages: 1 }
    : Array.isArray(root?.content)
      ? root
      : Array.isArray(root?.data?.content)
        ? root.data
        : Array.isArray(root?.items)
          ? { content: root.items, totalElements: root.total ?? root.items.length, totalPages: root.totalPages }
          : Array.isArray(root?.data)
            ? { content: root.data, totalElements: root.total ?? root.data.length, totalPages: root.totalPages }
            : { content: [] };

  const items = Array.isArray(container.content) ? container.content : [];
  const totalElements = Number(container.totalElements ?? container.total ?? items.length) || items.length;
  const totalPages = Number(container.totalPages ?? Math.max(1, Math.ceil(totalElements / (container.size || pageSize || 1)))) || 1;

  return {
    items,
    totalElements,
    totalPages,
  };
};

const matchesClientFilters = (certificate, filters) => {
  const standardLabel = buildStandardLabel(certificate).toUpperCase();
  const relevantDate = toDate(certificate.paymentDate || certificate.confirmedAt || certificate.issueDate || certificate.createdAt);

  if (filters.standard && !standardLabel.includes(filters.standard.toUpperCase())) {
    return false;
  }

  if (filters.dateFrom) {
    const fromDate = toDate(filters.dateFrom);
    if (fromDate && relevantDate && relevantDate < fromDate) {
      return false;
    }
  }

  if (filters.dateTo) {
    const toDateValue = toDate(filters.dateTo);
    if (toDateValue && relevantDate && relevantDate > toDateValue) {
      return false;
    }
  }

  return true;
};

const buildPageItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) pages.push('left-ellipsis');
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < totalPages - 1) pages.push('right-ellipsis');
  pages.push(totalPages);

  return pages;
};

const buildFinderCells = (size, originRow, originCol) => {
  const filled = new Set();
  for (let row = originRow; row < originRow + 7; row += 1) {
    for (let col = originCol; col < originCol + 7; col += 1) {
      const isBorder = row === originRow || row === originRow + 6 || col === originCol || col === originCol + 6;
      const isCenter = row >= originRow + 2 && row <= originRow + 4 && col >= originCol + 2 && col <= originCol + 4;
      if (row >= 0 && row < size && col >= 0 && col < size && (isBorder || isCenter)) {
        filled.add(`${row}-${col}`);
      }
    }
  }
  return filled;
};

const PseudoQrCode = ({ value }) => {
  const cellSize = 6;
  const size = 21;
  const finderCells = useMemo(() => {
    const merged = new Set();
    [
      ...buildFinderCells(size, 0, 0),
      ...buildFinderCells(size, 0, size - 7),
      ...buildFinderCells(size, size - 7, 0),
    ].forEach((entry) => merged.add(entry));
    return merged;
  }, []);

  const cells = useMemo(() => {
    const seed = String(value || 'CBQA');
    const result = [];
    for (let row = 0; row < size; row += 1) {
      for (let col = 0; col < size; col += 1) {
        const key = `${row}-${col}`;
        if (finderCells.has(key)) {
          result.push(key);
          continue;
        }
        const charCode = seed.charCodeAt((row * size + col) % seed.length) || 0;
        const shouldFill = ((charCode + row * 7 + col * 11) % 5) < 2;
        if (shouldFill) result.push(key);
      }
    }
    return result;
  }, [finderCells, value]);

  return (
    <svg width={size * cellSize} height={size * cellSize} viewBox={`0 0 ${size * cellSize} ${size * cellSize}`} role="img" aria-label={`QR code for ${value}`}>
      <rect width="100%" height="100%" fill="#fff" rx="8" />
      {cells.map((entry) => {
        const [row, col] = entry.split('-').map(Number);
        return (
          <rect
            key={entry}
            x={col * cellSize}
            y={row * cellSize}
            width={cellSize}
            height={cellSize}
            fill="#111827"
          />
        );
      })}
    </svg>
  );
};

const PseudoBarcode = ({ value }) => {
  const text = String(value || 'CBQA').toUpperCase();
  const bars = Array.from({ length: Math.max(28, text.length * 4) }, (_, index) => {
    const charCode = text.charCodeAt(index % text.length) || 0;
    return ((charCode + index * 13) % 4) + 1;
  });

  return (
    <svg width="100%" height="96" viewBox="0 0 320 96" role="img" aria-label={`Barcode for ${value}`}>
      <rect width="320" height="96" fill="#fff" rx="8" />
      {bars.map((barWidth, index) => {
        const x = 12 + (index * 10);
        const height = index % 3 === 0 ? 68 : 78;
        return (
          <rect
            key={`${value}-${index}`}
            x={x}
            y={10}
            width={barWidth}
            height={height}
            fill="#111827"
          />
        );
      })}
      <text x="160" y="90" textAnchor="middle" fontSize="12" fontFamily="monospace" fill="#334155">{text}</text>
    </svg>
  );
};

const FinanceConfirmation = () => {
  const alertTimerRef = useRef(null);
  const { hasRole, loading: authLoading } = useAuth();
  const canViewPage = hasRole('ROLE_FINANCE') || hasRole('ROLE_ADMIN') || hasRole('ROLE_DIRECTOR');

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [alert, setAlert] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalElements: 0,
    totalPages: 1,
  });

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
    if (alertTimerRef.current) {
      window.clearTimeout(alertTimerRef.current);
    }
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4000);
  }, []);

  useEffect(() => () => {
    if (alertTimerRef.current) {
      window.clearTimeout(alertTimerRef.current);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/audit/certificates', {
        params: {
          status: 'PENDING_PAYMENT',
          standard: appliedFilters.standard || undefined,
          dateFrom: appliedFilters.dateFrom || undefined,
          dateTo: appliedFilters.dateTo || undefined,
          page: Math.max(0, pagination.currentPage - 1),
          size: pagination.pageSize,
        },
      });

      const normalized = normalizeListResponse(response.data, pagination.pageSize);
      const filteredItems = normalized.items.filter((item) => matchesClientFilters(item, appliedFilters));
      const shouldUseFallbackTotals = normalized.items.length === normalized.totalElements;

      setRows(filteredItems);
      setPagination((prev) => ({
        ...prev,
        totalElements: shouldUseFallbackTotals ? filteredItems.length : normalized.totalElements,
        totalPages: shouldUseFallbackTotals
          ? Math.max(1, Math.ceil(filteredItems.length / (prev.pageSize || 1)))
          : Math.max(1, normalized.totalPages),
      }));
    } catch (error) {
      console.error('Failed to load finance confirmation rows:', error);
      setRows([]);
      setPagination((prev) => ({ ...prev, totalElements: 0, totalPages: 1 }));
      showAlert('danger', error?.response?.data?.message || 'Failed to load pending payment certificates.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, pagination.currentPage, pagination.pageSize, showAlert]);

  useEffect(() => {
    if (authLoading || !canViewPage) return;
    fetchRows();
  }, [authLoading, canViewPage, fetchRows]);

  const pageItems = useMemo(
    () => buildPageItems(pagination.currentPage, pagination.totalPages),
    [pagination.currentPage, pagination.totalPages],
  );

  const openPreview = (certificate = {}) => {
    const previewPayload = {
      ...certificate,
      barcodeValue: certificate.barcodeValue || certificate.barcode || resolveCertificateNumber(certificate),
      qrValue: certificate.qrValue || certificate.qrCode || buildProjectCode(certificate),
    };
    setPreviewData(previewPayload);
    setIsPreviewOpen(true);
  };

  const handleConfirmPayment = async (certificate) => {
    const projectId = resolveProjectId(certificate);
    if (!projectId) {
      showAlert('danger', 'Project ID was not found for this certificate.');
      return;
    }

    setConfirmingId(projectId);
    try {
      const response = await api.post(`/audit/projects/${projectId}/certificate/confirm`);
      const confirmedData = response?.data?.data || response?.data || {};
      openPreview({
        ...certificate,
        ...confirmedData,
        paymentStatus: 'CONFIRMED',
        certificateStatus: confirmedData.certificateStatus || confirmedData.status || 'CONFIRMED',
        confirmedAt: confirmedData.confirmedAt || new Date().toISOString(),
      });
      showAlert('success', 'Payment confirmed successfully.');
      await fetchRows();
    } catch (error) {
      console.error('Failed to confirm payment:', error);
      showAlert('danger', error?.response?.data?.message || 'Failed to confirm payment.');
    } finally {
      setConfirmingId(null);
    }
  };

  if (authLoading) {
    return (
      <div className="container-xl py-5 text-center text-muted">
        <Loader className="icon icon-spin mx-auto mb-2" />
        Loading finance confirmation...
      </div>
    );
  }

  if (!canViewPage) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container-xl">
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <Modal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title={previewData?.confirmedAt ? 'Payment Confirmed' : 'Certificate Preview'}
        size="lg"
      >
        <div className="modal-body">
          {previewData && (
            <div className="row g-4">
              <div className="col-12">
                <div className="alert alert-success mb-0">
                  Certificate generation has been triggered and the barcode / QR preview is ready.
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 bg-light h-100">
                  <div className="card-body text-center">
                    <div className="text-uppercase text-muted small mb-2">QR Preview</div>
                    <PseudoQrCode value={previewData.qrValue || buildProjectCode(previewData)} />
                    <div className="mt-3 fw-semibold">{previewData.qrValue || buildProjectCode(previewData)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 bg-light h-100">
                  <div className="card-body text-center">
                    <div className="text-uppercase text-muted small mb-2">Barcode Preview</div>
                    <PseudoBarcode value={previewData.barcodeValue || resolveCertificateNumber(previewData)} />
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="text-muted small">Project Code</div>
                    <div className="fw-semibold">{buildProjectCode(previewData)}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">Client Name</div>
                    <div className="fw-semibold">{buildClientName(previewData)}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">Confirmed At</div>
                    <div className="fw-semibold">{formatDate(previewData.confirmedAt)}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-primary" onClick={() => setIsPreviewOpen(false)}>
            Close
          </button>
        </div>
      </Modal>

      <div className="row g-2 align-items-center mb-3">
        <div className="col">
          <h2 className="page-title mb-1">Finance Confirmation</h2>
          <div className="text-muted">Confirm certificate payments and preview generated verification assets.</div>
        </div>
        <div className="col-auto">
          <button type="button" className="btn btn-outline-secondary" onClick={fetchRows} disabled={loading}>
            {loading ? <Loader className="icon icon-spin" size={16} /> : <RefreshCcw size={16} className="me-1" />}
            Refresh
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body border-bottom">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label">Standard</label>
              <select
                className="form-select"
                value={filters.standard}
                onChange={(event) => setFilters((prev) => ({ ...prev, standard: event.target.value }))}
              >
                <option value="">All standards</option>
                {STANDARD_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label">Date from</label>
              <input
                type="date"
                className="form-control"
                value={filters.dateFrom}
                onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label">Date to</label>
              <input
                type="date"
                className="form-control"
                value={filters.dateTo}
                onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
              />
            </div>
            <div className="col-12 col-md-2 d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary flex-fill"
                onClick={() => {
                  setAppliedFilters(filters);
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }}
                disabled={loading}
              >
                Apply
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary flex-fill"
                onClick={() => {
                  setFilters(DEFAULT_FILTERS);
                  setAppliedFilters(DEFAULT_FILTERS);
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }}
                disabled={loading}
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 72 }}>No</th>
                <th>Project Code</th>
                <th>Client Name</th>
                <th>Standard</th>
                <th>Fee Amount</th>
                <th>Payment Status</th>
                <th>Certificate Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-5 text-muted">
                    <Loader className="icon icon-spin mx-auto mb-2 d-block" />
                    Loading pending confirmations...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-5 text-muted">
                    No finance confirmation items found.
                  </td>
                </tr>
              ) : rows.map((row, index) => {
                const confirmed = hasPreviewAssets(row);
                const projectId = resolveProjectId(row);
                const paymentStatus = confirmed ? 'Confirmed' : (row.paymentStatus || 'Pending Payment');
                const certificateStatus = confirmed ? 'Confirmed' : (row.certificateStatus || row.status || 'Pending Payment');
                const rowNumber = ((pagination.currentPage - 1) * pagination.pageSize) + index + 1;

                return (
                  <tr key={row.id || `${buildProjectCode(row)}-${rowNumber}`}>
                    <td>{rowNumber}</td>
                    <td className="fw-semibold text-primary">{buildProjectCode(row)}</td>
                    <td>{buildClientName(row)}</td>
                    <td>{buildStandardLabel(row)}</td>
                    <td>{formatAmount(row.feeAmount || row.amount || row.certificateFee, row.currencyCode || row.displayCurrencyCode || 'IDR')}</td>
                    <td>
                      <span className={`badge ${confirmed ? 'bg-success' : 'bg-warning text-dark'}`}>{paymentStatus}</span>
                    </td>
                    <td>
                      <span className={`badge ${confirmed ? 'bg-success' : 'bg-secondary'}`}>{certificateStatus}</span>
                    </td>
                    <td className="text-end">
                      <div className="d-inline-flex gap-2">
                        {confirmed ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openPreview(row)}
                          >
                            <Eye size={14} className="me-1" />
                            View preview
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => handleConfirmPayment(row)}
                            disabled={!projectId || confirmingId === projectId}
                          >
                            {confirmingId === projectId ? <Loader size={14} className="icon icon-spin me-1" /> : null}
                            Confirm Payment
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="card-footer d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <div className="text-muted small">
            Showing {rows.length === 0 ? 0 : ((pagination.currentPage - 1) * pagination.pageSize) + 1}
            –{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalElements)} of {pagination.totalElements} items
          </div>

          <div className="d-flex align-items-center gap-3 ms-md-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 88 }}
                value={pagination.pageSize}
                onChange={(event) => {
                  const pageSize = Number(event.target.value);
                  setPagination((prev) => ({ ...prev, pageSize, currentPage: 1 }));
                }}
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <nav aria-label="Finance confirmation pagination">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => setPagination((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                    disabled={pagination.currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {pageItems.map((page) => (
                  typeof page === 'number' ? (
                    <li key={page} className={`page-item ${pagination.currentPage === page ? 'active' : ''}`}>
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => setPagination((prev) => ({ ...prev, currentPage: page }))}
                      >
                        {page}
                      </button>
                    </li>
                  ) : (
                    <li key={page} className="page-item disabled"><span className="page-link">…</span></li>
                  )
                ))}
                <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => setPagination((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                    disabled={pagination.currentPage === pagination.totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceConfirmation;
